import { Input } from "@/components/ui/input";
import { useEffect, useState } from "react";

interface InlineTextInputProps {
    value?: string;
    /** Fired only on a committed, actually-changed value. */
    onCommit: (value: string) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
}

/**
 * Free-text cell edited in place: commits on blur or Enter, reverts on Escape.
 * Deliberately not committing per keystroke — each commit is one API mutation.
 */
export function InlineTextInput({ value, onCommit, placeholder, className, disabled }: InlineTextInputProps) {
    const [text, setText] = useState(value ?? "");

    // Resync when the value changes from outside — a refetch after a successful
    // save, or the row being reused for a different staff member after paging.
    useEffect(() => {
        setText(value ?? "");
    }, [value]);

    const commit = () => {
        const next = text.trim();
        if (next === (value ?? "").trim()) {
            setText(value ?? "");
            return;
        }
        setText(next);
        onCommit(next);
    };

    return (
        <Input
            value={text}
            disabled={disabled}
            placeholder={placeholder}
            autoComplete="off"
            className={className}
            onChange={(e) => setText(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                    e.currentTarget.blur();
                    return;
                }
                // Revert without blurring: blurring here would run commit() against
                // the stale text still captured in this render's closure.
                if (e.key === "Escape") setText(value ?? "");
            }}
        />
    );
}
